import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, DownloadIcon, MailCheckIcon, ChevronRightIcon } from 'lucide-react';
import { Container, Kicker, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../components/ui/Field';
import { getResource } from '../data/resources';
import { SECTORS } from '../data/services';
import { submitLead, requestDownloadUrl } from '../lib/leads';
import { track } from '../lib/analytics';
import { Seo } from '../components/Seo';
import { resourceSchema, breadcrumbSchema } from '../lib/seo';
import { NotFound } from './NotFound';
export function ResourcePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const resource = getResource(slug ?? '');
  const [email, setEmail] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [role, setRole] = React.useState('');
  const [sector, setSector] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);
  const [signedLink, setSignedLink] = React.useState(false);
  if (!resource) return <NotFound data-id="element-3303" data-mp="3c43ivv1h13yb" />;
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await submitLead({
      email,
      firstName,
      organisation,
      role,
      sector,
      consent,
      source: `resource:${resource.slug}`
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Something went wrong. Please try again.');
      return;
    }
    const download = await requestDownloadUrl(resource.fileName, {
      kind: 'resource',
      id: resource.slug
    }, resource.hostedFileUrl);
    if (!download.url) {
      setError(download.error ?? 'We could not prepare your download. Please try again.');
      return;
    }
    setSignedLink(Boolean(download.signed));
    setDownloadUrl(download.url);
    track('ebook_download', {
      resource: resource.slug,
      sector
    });
  };
  return <>
      <Seo title={`${resource.title}, free ${resource.format.toLowerCase()}`} description={resource.summary} path={`/resources/${resource.slug}`} image={resource.cover} jsonLd={[resourceSchema({
      title: resource.title,
      description: resource.summary,
      path: `/resources/${resource.slug}`,
      image: resource.cover
    }), breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Resources',
      path: '/resources'
    }, {
      name: resource.title,
      path: `/resources/${resource.slug}`
    }])]} data-id="element-3304" data-mp="2kvablmef1hg0" />

      <section className="surface-dark" data-id="element-3305" data-mp="1s51uzyzj353j">
        <Container className="py-12 sm:py-16" data-id="element-3306" data-mp="1kn2i4qi886j0">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-3307" data-mp="1kpyielhjr7am">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-3308" data-mp="1fnr3bn1f7b42">
              <li data-id="element-3309" data-mp="1umbchkh07n5w"><Link to="/" className="hover:text-white" data-id="element-3310" data-mp="1ul08bjjncde9">Home</Link></li>
              <li aria-hidden="true" data-id="element-3311" data-mp="1pn85huhgr6va"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-3312" data-mp="1pmoz9b16e6x3" /></li>
              <li data-id="element-3313" data-mp="11xdtff4yxmc3"><Link to="/resources" className="hover:text-white" data-id="element-3314" data-mp="11xduzjkm9ue7">Resources</Link></li>
              <li aria-hidden="true" data-id="element-3315" data-mp="11v1elauibs39"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-3316" data-mp="wyjeioa4ghx9" /></li>
              <li aria-current="page" className="truncate text-white/80" data-id="element-3317" data-mp="x044s1pelepb">{resource.title}</li>
            </ol>
          </nav>
          <Kicker data-id="element-3318" data-mp="16wh1l8g8w8oa">{resource.format}</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[32px] leading-tight text-white sm:text-[44px]" data-id="element-3319" data-mp="16vflgpkdgvzp">
            {resource.title}
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-3320" data-mp="e7t9ou0zql1r">{resource.summary}</p>
        </Container>
      </section>

      <Container className="py-14 sm:py-20" data-id="element-3321" data-mp="3dmc725z4r5s1">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-3322" data-mp="3dnncea884ll9">
          <div data-id="element-3323" data-mp="3hmmabavjfi37">
            <img src={resource.cover} alt={resource.coverAlt} className="w-full max-w-[380px] rounded-md" data-id="element-3324" data-mp="3ho795covxn6v" />
            <h2 className="mt-10 font-display text-[26px] text-ink" data-id="element-3325" data-mp="3rdqrmh8orf17">What is inside</h2>
            <ul className="mt-5 space-y-3" data-id="element-3326" data-mp="6wazb1i01z0">
              {resource.whatIsInside.map(item => <li key={item} className="flex gap-3 text-[16px] text-body" data-id="element-3327" data-mp="55zayh2mfi04">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-3328" data-mp="583j5b7jz367" />
                  {item}
                </li>)}
            </ul>
            <h2 className="mt-10 font-display text-[26px] text-ink" data-id="element-3329" data-mp="e65htk3tdjqb">Who it is for</h2>
            <p className="mt-4 max-w-prose text-[16px] text-body" data-id="element-3330" data-mp="2b00ll4y0agox">{resource.forWho}</p>
            <p className="mt-4 text-small text-muted" data-id="element-3331" data-mp="2fx8vki903ef3">
              {resource.pages} pages. Delivered as a PDF through a secure link that expires after fifteen minutes.
            </p>
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-3332" data-mp="23nj9x88eu059">
            <div className="rounded-md border border-line bg-mist p-6 sm:p-8" data-id="element-3333" data-mp="1yopsj4q05ozt">
              {downloadUrl ? <div data-id="element-3334" data-mp="1tpwpy7c97ief">
                  <div className="flex items-center gap-2 text-gold" data-id="element-3335" data-mp="1tqo9gjsktpoc">
                    <MailCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-3336" data-mp="2rd55llpkpx8i" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-3337" data-mp="2rbaraq8t53de">Ready</span>
                  </div>
                  <h2 className="mt-4 font-display text-[24px] leading-tight text-ink" data-id="element-3338" data-mp="2hexqz9irbr9o">
                    Your download is ready
                  </h2>
                  <p className="mt-3 text-[15px] text-body" data-id="element-3339" data-mp="2ddchly5flv48">
                    {signedLink ? `We have also emailed the link to ${email}, so you can come back to it. The link expires after fifteen minutes and can be reissued from that email.` : `Thank you${firstName ? `, ${firstName}` : ''}. Your copy opens in a new tab, and you can save it from there.`}
                  </p>
                  <a href={downloadUrl} target="_blank" rel="noopener noreferrer" download={resource.fileName} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-gold px-5 py-3.5 text-[15px] font-medium text-charcoal transition-colors hover:bg-[#ff7440]" data-id="element-3340" data-mp="18ccwwb6lyfq6">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-3341" data-mp="aqytznueomp3" />
                    Download the PDF
                    <span className="sr-only" data-id="element-3342" data-mp="fps3z6ahhvki">(opens in a new tab)</span>
                  </a>
                  {signedLink && <p className="mt-4 break-all text-small text-muted" data-id="element-3343" data-mp="1pqx9a7jftx4">Secure link: {downloadUrl}</p>}
                  <div className="mt-6 border-t border-line pt-6" data-id="element-3344" data-mp="5s3z3nj5z7om">
                    <Button to="/contact" variant="secondary" className="w-full" data-id="element-3345" data-mp="5toxjo8lnarr">
                      Book a discovery call
                    </Button>
                  </div>
                </div> : <form onSubmit={onSubmit} className="space-y-5" data-id="element-3346" data-mp="3s9e18oe7lwzn">
                  <div data-id="element-3347" data-mp="3s7ji9fmi0ey7">
                    <h2 className="font-display text-[24px] leading-tight text-ink" data-id="element-3348" data-mp="3nat3cvmfjped">Get your copy</h2>
                    <p className="mt-2 text-[15px] text-body" data-id="element-3349" data-mp="6265mdt3d4gt">
                      Five fields. We use them to send the file and to tailor what we send you next.
                    </p>
                  </div>

                  <Field id="res-email" label="Work email" required error={error ?? undefined} data-id="element-3350" data-mp="ev9gceccr37r">
                    <input id="res-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" className={inputClass} data-id="element-3351" data-mp="asmj1xwwwq36" />
                  </Field>
                  <Field id="res-name" label="First name" required data-id="element-3352" data-mp="1v7lkw5etb9et">
                    <input id="res-name" type="text" required value={firstName} onChange={event => setFirstName(event.target.value)} className={inputClass} data-id="element-3353" data-mp="1v9y8oz794dzs" />
                  </Field>
                  <Field id="res-org" label="Organisation" required data-id="element-3354" data-mp="1cgucp76k9p20">
                    <input id="res-org" type="text" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-3355" data-mp="1cf13pl8h6squ" />
                  </Field>
                  <Field id="res-role" label="Your role" required data-id="element-3356" data-mp="tq49tigzq295">
                    <input id="res-role" type="text" required value={role} onChange={event => setRole(event.target.value)} className={inputClass} data-id="element-3357" data-mp="m7d3b4pnjg0z" />
                  </Field>
                  <Field id="res-sector" label="Sector" required data-id="element-3358" data-mp="7avtihx429gj">
                    <select id="res-sector" required value={sector} onChange={event => setSector(event.target.value)} className={inputClass} data-id="element-3359" data-mp="38ipbxy0ijlv">
                      <option value="" data-id="element-3360" data-mp="3podb04fn5oo7">Please choose</option>
                      {SECTORS.map(item => <option key={item} value={item} data-id="element-3361" data-mp="3kn5zbuado5c9">
                          {item}
                        </option>)}
                    </select>
                  </Field>

                  <ConsentCheckbox id="res-consent" checked={consent} onChange={setConsent} data-id="element-3362" data-mp="3bqon0nqa21ex">
                    I am happy for Cavalla Solution to email me about this download and related insights. See our{' '}
                    <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-3363" data-mp="38b53sbfymbrv">
                      privacy notice
                    </Link>
                    .
                  </ConsentCheckbox>

                  <Button type="submit" size="lg" disabled={submitting} className="w-full" data-id="element-3364" data-mp="2fm7lw9fnugzn">
                    {submitting ? 'Preparing your file' : 'Send me the download'}
                  </Button>
                </form>}
            </div>

            <Callout className="mt-6" data-id="element-3365" data-mp="2oogz572bcogq">
              Files are held in private storage. Every download is issued through a signed link generated for that
              request, so the file path is never exposed.
            </Callout>
          </div>
        </div>
      </Container>
    </>;
}